import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  ClipboardList,
  Lock,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { JudgeSheetPrintable } from '@/components/events/JudgeSheetPrintable';
import {
  useJudgeSheetTemplates,
  useCreateJudgeSheetTemplate,
  useDeleteJudgeSheetTemplate,
  useUpdateJudgeSheetTemplate,
  type JudgeSheetTemplateRecord,
} from '@/api/extensions/useJudgeSheetTemplates';
import type { JudgeSheetColumn } from '@/lib/judge-sheet-templates';

interface EditorState {
  id: string | null;
  slug: string;
  display_name: string;
  description: string;
  columns: JudgeSheetColumn[];
  min_rows: number;
}

const EMPTY_EDITOR: EditorState = {
  id: null,
  slug: '',
  display_name: '',
  description: '',
  columns: [{ label: 'COLUMN 1' }],
  min_rows: 17,
};

const SAMPLE_CONTESTANTS = [
  { position: 1, name: 'Sample Contestant A' },
  { position: 2, name: 'Sample Contestant B' },
];

const JudgeSheetTemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: templates, isLoading } = useJudgeSheetTemplates();
  const createMutation = useCreateJudgeSheetTemplate();
  const updateMutation = useUpdateJudgeSheetTemplate();
  const deleteMutation = useDeleteJudgeSheetTemplate();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editor, setEditor] = useState<EditorState>(EMPTY_EDITOR);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const sortedTemplates = useMemo(() => {
    return [...(templates ?? [])].sort((a, b) => {
      if (a.is_system !== b.is_system) return a.is_system ? -1 : 1;
      return a.display_name.localeCompare(b.display_name);
    });
  }, [templates]);

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isEditing = editor.id !== null;

  function openNewEditor() {
    setEditor({ ...EMPTY_EDITOR });
    setSaveError(null);
    setEditorOpen(true);
  }

  function openEditEditor(t: JudgeSheetTemplateRecord) {
    setEditor({
      id: t.id,
      slug: t.slug,
      display_name: t.display_name,
      description: t.description ?? '',
      columns: t.columns.map((c) => ({ label: c.label, widthClass: c.widthClass })),
      min_rows: t.min_rows,
    });
    setSaveError(null);
    setEditorOpen(true);
  }

  function moveColumn(idx: number, direction: -1 | 1) {
    const target = idx + direction;
    if (target < 0 || target >= editor.columns.length) return;
    const next = [...editor.columns];
    [next[idx], next[target]] = [next[target], next[idx]];
    setEditor((s) => ({ ...s, columns: next }));
  }

  function updateColumnLabel(idx: number, label: string) {
    setEditor((s) => ({
      ...s,
      columns: s.columns.map((c, i) => (i === idx ? { ...c, label } : c)),
    }));
  }

  function addColumn() {
    setEditor((s) => ({
      ...s,
      columns: [...s.columns, { label: `COLUMN ${s.columns.length + 1}` }],
    }));
  }

  function removeColumn(idx: number) {
    setEditor((s) => ({ ...s, columns: s.columns.filter((_, i) => i !== idx) }));
  }

  async function handleSave() {
    setSaveError(null);
    const trimmedColumns = editor.columns.map((c) => ({
      label: c.label.trim(),
      ...(c.widthClass ? { width_class: c.widthClass } : {}),
    }));
    if (trimmedColumns.some((c) => !c.label)) {
      setSaveError('Every column needs a label.');
      return;
    }
    if (!editor.display_name.trim()) {
      setSaveError('Display name is required.');
      return;
    }

    try {
      if (editor.id) {
        await updateMutation.mutateAsync({
          id: editor.id,
          input: {
            display_name: editor.display_name.trim(),
            description: editor.description.trim() || null,
            columns: trimmedColumns,
            min_rows: editor.min_rows,
          },
        });
      } else {
        const slug = (editor.slug || editor.display_name)
          .toLowerCase()
          .replace(/[^a-z0-9_-]+/g, '_')
          .replace(/^_+|_+$/g, '');
        if (!slug) {
          setSaveError('Could not derive a slug from the display name.');
          return;
        }
        await createMutation.mutateAsync({
          slug,
          display_name: editor.display_name.trim(),
          description: editor.description.trim() || null,
          columns: trimmedColumns,
          min_rows: editor.min_rows,
        });
      }
      setEditorOpen(false);
    } catch (err: any) {
      setSaveError(err?.message ?? 'Failed to save template.');
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteMutation.mutateAsync(id);
      setConfirmDeleteId(null);
    } catch (err: any) {
      // Server returns 403 for system rows; surface inline.
      setSaveError(err?.message ?? 'Failed to delete template.');
    }
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/settings/system')}
            className="mt-1"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Judges Sheet Templates</h1>
            <p className="mt-2 text-muted-foreground">
              Column layouts used when printing the judge's sheet for an event.
              System templates ship with the app and can't be edited or deleted.
            </p>
          </div>
        </div>
        <Button onClick={openNewEditor}>
          <Plus className="h-4 w-4 mr-1" />
          New Template
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : sortedTemplates.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title="No templates yet"
          description="Create your first custom judge-sheet template."
        />
      ) : (
        <div className="grid gap-3">
          {sortedTemplates.map((t) => (
            <Card key={t.id}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold truncate">{t.display_name}</h3>
                    {t.is_system && (
                      <Badge variant="secondary" className="text-xs">
                        <Lock className="h-3 w-3 mr-1" />
                        System
                      </Badge>
                    )}
                    {t.organization_id === null && !t.is_system && (
                      <Badge variant="outline" className="text-xs">Global</Badge>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground truncate">
                    {t.description || `${t.columns.length} columns · ${t.min_rows} rows`}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground/80 font-mono truncate">
                    {t.columns.map((c) => c.label).join(' · ')}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditEditor(t)}
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1" />
                    {t.is_system ? 'View' : 'Edit'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={t.is_system}
                    onClick={() => setConfirmDeleteId(t.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Editor dialog */}
      <Dialog open={editorOpen} onOpenChange={(open) => !open && setEditorOpen(false)}>
        <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogTitle>
            {isEditing ? 'Edit Template' : 'New Template'}
          </DialogTitle>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6">
            {/* Editor */}
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="display_name">Display name</Label>
                <Input
                  id="display_name"
                  value={editor.display_name}
                  onChange={(e) =>
                    setEditor((s) => ({ ...s, display_name: e.target.value }))
                  }
                  placeholder="e.g. Steer Wrestling"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="description">Description (optional)</Label>
                <Input
                  id="description"
                  value={editor.description}
                  onChange={(e) =>
                    setEditor((s) => ({ ...s, description: e.target.value }))
                  }
                  placeholder="What this layout is for"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="min_rows">Minimum rows on printed sheet</Label>
                <Input
                  id="min_rows"
                  type="number"
                  min={1}
                  max={100}
                  value={editor.min_rows}
                  onChange={(e) =>
                    setEditor((s) => ({
                      ...s,
                      min_rows: Math.max(1, Math.min(100, Number(e.target.value) || 17)),
                    }))
                  }
                  className="w-32"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Columns</Label>
                  <Button variant="outline" size="sm" onClick={addColumn}>
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Add column
                  </Button>
                </div>
                <div className="space-y-2">
                  {editor.columns.map((col, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input
                        value={col.label}
                        onChange={(e) => updateColumnLabel(idx, e.target.value)}
                        placeholder="Column header"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => moveColumn(idx, -1)}
                        disabled={idx === 0}
                        aria-label="Move column up"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => moveColumn(idx, 1)}
                        disabled={idx === editor.columns.length - 1}
                        aria-label="Move column down"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeColumn(idx)}
                        disabled={editor.columns.length <= 1}
                        aria-label="Remove column"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {saveError && (
                <div className="text-sm text-red-600">{saveError}</div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditorOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving ? 'Saving…' : isEditing ? 'Save Changes' : 'Create Template'}
                </Button>
              </div>
            </div>

            {/* Live preview — clipped to the scaled US-Letter dimensions so
                the print component renders at full intrinsic size internally
                but visually fits inside the editor's right column. */}
            <div className="border rounded-md p-3 bg-neutral-50">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                Live preview
              </div>
              <div
                style={{
                  width: 'calc(8.5in * 0.42)',
                  height: 'calc(11in * 0.42)',
                  overflow: 'hidden',
                  background: 'white',
                }}
              >
                <div
                  style={{
                    transform: 'scale(0.42)',
                    transformOrigin: 'top left',
                    width: '8.5in',
                    height: '11in',
                  }}
                >
                  <JudgeSheetPrintable
                    inline
                    organizationName="Sample Organization"
                    rodeoName="Preview Rodeo"
                    roundLabel="1st Go Round"
                    eventTypeName={editor.display_name || 'Sample Event'}
                    eventDate="Friday, October 3, 2025"
                    columns={editor.columns.map((c) => ({ label: c.label || '—' }))}
                    minRows={editor.min_rows}
                    contestants={SAMPLE_CONTESTANTS}
                  />
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={!!confirmDeleteId}
        onOpenChange={(open) => !open && setConfirmDeleteId(null)}
      >
        <DialogContent>
          <DialogTitle>Delete template?</DialogTitle>
          <p className="text-sm text-muted-foreground">
            This will remove the template. Events that referenced it will fall
            back to their auto-classified default until you assign a new one.
          </p>
          <div className="flex items-center justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setConfirmDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => confirmDeleteId && handleDelete(confirmDeleteId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JudgeSheetTemplatesPage;
